import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { aiService } from '../../services/aiService';
import { DocumentUpload } from './DocumentUpload';
import { ExtractedDataPreview } from './ExtractedDataPreview';
import type { AiExtractResponse, Contract } from '@shared/types';

export function AiScopeAssist() {
  const navigate = useNavigate();
  const [extracting, setExtracting] = useState(false);
  const [extractedData, setExtractedData] = useState<AiExtractResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleExtract = async (textContent: string) => {
    try {
      setExtracting(true);
      setError(null);
      const result = await aiService.extractFromDocument({ textContent });
      setExtractedData(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to extract data');
    } finally {
      setExtracting(false);
    }
  };

  const handleCreateContract = () => {
    if (!extractedData) return;
    
    // Navigate to contract wizard with pre-filled data
    navigate('/contracts/new', {
      state: {
        prefillData: {
          executiveSummary: extractedData.executiveSummary,
          deliverables: extractedData.deliverables,
          milestones: extractedData.milestones,
          payments: extractedData.payments,
        },
      },
    });
  };

  const handleReset = () => {
    setExtractedData(null);
    setError(null);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-5xl mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">AI Scope Assistant</h1>
          <p className="mt-2 text-gray-600">
            Extract structured contract data from documents using AI
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div>
            <DocumentUpload
              onExtract={handleExtract}
              extracting={extracting}
              disabled={extracting}
            />
            {error && (
              <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-700">{error}</p>
              </div>
            )}
          </div>

          <div>
            {extractedData ? (
              <ExtractedDataPreview
                data={extractedData}
                onCreateContract={handleCreateContract}
                onReset={handleReset}
              />
            ) : (
              <div className="bg-white rounded-lg shadow-sm p-12 text-center text-gray-500">
                <p>Upload a document to see extracted data here</p>
              </div>
            )}
          </div>
        </div>

        <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h3 className="font-semibold text-blue-900 mb-2">How it works</h3>
          <ul className="space-y-2 text-sm text-blue-800">
            <li>• Paste contract text or upload a document</li>
            <li>• AI extracts key information: summary, deliverables, milestones, payments</li>
            <li>• Review and edit the extracted data</li>
            <li>• Create a contract with pre-filled information</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

